import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  Stack,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useDispatch } from 'react-redux';

import { Strings, taskOverviewColors } from '@app/constants';
import { TaskOverviewCard } from '@app/components/card';
import { DataNotFound, Scrollbar, StatusTag } from '@app/components';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { StyledTitle } from '@app/modules/dashboard/style';
import { IManagerDashboard, IProject } from '../type';
import { CustomAvatarGroup, CustomAvatar } from '@app/components/custom-avatar';
import { useRequestState } from '@app/hooks';
import { getTasksOverview, selectGetTasksOverviewState } from '@app/modules/dashboard';

interface Props {
  data?: IManagerDashboard;
}

const MangerProjectOverview = ({ data }: Props) => {
  const theme = useTheme();

  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [resetSelectedMember, setResetSelectedMember] = useState(false);

  const tasksColors = taskOverviewColors(theme);

  const dispatch = useDispatch();

  const { data: Tasks } = useRequestState({
    stateSelector: selectGetTasksOverviewState,
    errorShown: true,
  });

  useEffect(() => {
    if (data && data?.projects?.projects?.length > 0) {
      const firstProjectId = data.projects.projects[0].id;
      setSelectedProject(firstProjectId);
      dispatch(getTasksOverview(firstProjectId));
    }
  }, [data, dispatch]);

  const handleCardClick = (id: number) => {
    setSelectedProject(id);
    dispatch(getTasksOverview(id));
    setResetSelectedMember(true);
  };

  const projectsWithUserNames = data?.projects?.projects?.map((project: IProject) => ({
    ...project,
    teamMembers: project?.members?.map((memberId: number) => {
      const user = data?.projects?.users.find((user: any) => user.id === memberId);
      return user ? user.firstName : `User ${memberId}`;
    }),
  }));

  return (
    <Card sx={{ borderRadius: '5px', margin: '0 2% 2% 2%' }}>
      <Stack flexDirection={{ sm: 'column', md: 'row' }} mb={3}>
        <Card
          sx={{
            width: { sm: '97%', md: '45%' },
            height: { md: '350px' },
            maxHeight: { xs: '350px' },
            margin: '0 2% 0 2%',
            marginTop: { xs: '2%', sm: '2%', md: '2%' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderRadius: '5px',
          }}
        >
          <StyledTitle>{Strings.employeeDashborad.myProjects}</StyledTitle>
          <Stack sx={{ height: '100%' }}>
            {projectsWithUserNames && projectsWithUserNames.length > 0 ? (
              <>
                <Scrollbar sx={{ height: 310, display: { xs: 'none', md: 'inline' } }}>
                  {projectsWithUserNames?.map((option: IProject) => (
                    <InfoStatusCard
                      key={option.id}
                      titleName={option.name}
                      subTitleAvatar={option.teamMembers}
                      status={option.priority}
                      image={option.image}
                      onClick={() => {
                        handleCardClick(option.id);
                      }}
                      isSelected={selectedProject === option.id}
                    />
                  ))}
                </Scrollbar>

                <Scrollbar sx={{ maxHeight: { xs: 310 }, display: { md: 'none', xs: 'inline' } }}>
                  {projectsWithUserNames?.map((option: IProject) => (
                    <Accordion
                      key={option.id}
                      onClick={() => {
                        handleCardClick(option.id);
                      }}
                    >
                      <Card
                        sx={{ borderRadius: '5px', margin: 1, height: 'auto' }}
                        onClick={() => {
                          handleCardClick(option.id);
                        }}
                      >
                        <Stack gap={1} justifyContent={'space-between'} direction={'row'} m={0.5}>
                          <Stack direction="row" gap={2}>
                            <CustomAvatar name={option.name} color="info" src={option.image} />
                            <Stack>
                              <Stack> {option.name}</Stack>
                              <Stack>
                                <CustomAvatarGroup max={4} size="tiny">
                                  {option.teamMembers?.map((member: any, index: number) => (
                                    <CustomAvatar key={index} name={member} color="info" />
                                  ))}
                                </CustomAvatarGroup>
                              </Stack>
                            </Stack>
                          </Stack>
                          <Stack direction={'row'}>
                            <Stack justifyContent={'center'} alignItems={'center'}>
                              <StatusTag status={option.priority ?? ''} />
                            </Stack>
                            <AccordionSummary
                              expandIcon={<ArrowDropDownIcon />}
                              aria-controls="panel2-content"
                              id="panel2-header"
                            ></AccordionSummary>
                          </Stack>
                        </Stack>
                      </Card>

                      <AccordionDetails>
                        <Stack
                          sx={{
                            width: { sm: '90%', md: '50%' },
                            height: '400px',
                            margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
                            marginTop: { xs: '2%', sm: '2%', md: '0%' },
                            borderRadius: '5px',
                          }}
                        >
                          <TaskOverviewCard
                            teamMembers={Tasks?.users ?? []}
                            color={tasksColors}
                            title={Strings.employeeDashborad.taskOverview}
                            taskValue={Tasks ?? []}
                            heading="manager"
                            resetSelectedMember={resetSelectedMember}
                            setResetSelectedMember={setResetSelectedMember}
                            selectedProject={selectedProject}
                          />
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Scrollbar>
              </>
            ) : (
              <Stack sx={{ height: '100%', justifyContent: 'center' }}>
                <DataNotFound height={200} />
              </Stack>
            )}
          </Stack>
        </Card>

        <Stack
          sx={{
            width: { sm: '90%', md: '50%' },
            height: '350px',
            justifyContent: 'center',
            alignItems: 'center',
            margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
            marginTop: { xs: '2%', sm: '2%', md: '0%' },
            borderRadius: '5px',
            display: { xs: 'none', md: 'inline' },
          }}
        >
          <TaskOverviewCard
            teamMembers={Tasks?.users ?? []}
            color={tasksColors}
            title={Strings.employeeDashborad.taskOverview}
            taskValue={Tasks ?? []}
            heading="manager"
            resetSelectedMember={resetSelectedMember}
            setResetSelectedMember={setResetSelectedMember}
            selectedProject={selectedProject}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

export { MangerProjectOverview };
